/**
 * Workspace API adapter — implements @neurograph/brief-ui's BriefApiClient
 * against the public token-gated endpoints (`/api/v1/public/brief/{token}/...`).
 *
 * Mirrors the brief-share SPA's local `api.ts` so the same chat / suggestion
 * components work inside the admin BriefWorkspace.  The only material
 * difference is that the admin mints its `edit` token transparently via
 * `briefShareApi.mint` and then uses it as the bearer credential here.
 */

import type {
  BriefApiClient,
  MentionablesPayload,
  SuggestionsResponse,
  BriefDraftResponse,
  QuickPatchResponse,
} from '@neurograph/brief-ui';

const base = (token: string) => `/api/v1/public/brief/${token}`;

async function handle<T>(r: Response): Promise<T> {
  if (!r.ok) {
    let detail: unknown = null;
    try {
      detail = await r.json();
    } catch {
      /* ignore */
    }
    const msg =
      (detail && typeof detail === 'object' && 'detail' in detail
        ? JSON.stringify((detail as Record<string, unknown>).detail)
        : `HTTP ${r.status}`) || `HTTP ${r.status}`;
    const err = new Error(msg) as Error & { status?: number; body?: unknown };
    err.status = r.status;
    err.body = detail;
    throw err;
  }
  return (await r.json()) as T;
}

export const workspaceBriefApi: BriefApiClient = {
  mentionables: (token) =>
    fetch(`${base(token)}/mentionables`).then((r) => handle<MentionablesPayload>(r)),
  suggestions: (token, personaId) => {
    const params = new URLSearchParams();
    if (personaId) params.set('persona_id', personaId);
    const qs = params.toString();
    return fetch(`${base(token)}/suggestions${qs ? `?${qs}` : ''}`).then((r) =>
      handle<SuggestionsResponse>(r),
    );
  },
  draft: (token, req) =>
    fetch(`${base(token)}/draft`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    }).then((r) => handle<BriefDraftResponse>(r)),
  quickPatch: (token, section, content) =>
    fetch(`${base(token)}/quick-patch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ section, content }),
    }).then((r) => handle<QuickPatchResponse>(r)),
};
